import { MenuIcon, MoreVerticalIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { t } from '../../i18n';
import { useLang } from '../hooks/useLang';
import type { LegalKind } from '../views/LegalSheet';

// The `min-[900px]:` breakpoints below are a Tailwind arbitrary variant, kept
// in sync by hand with `useIsDesktop.ts`'s `QUERY` — if that breakpoint ever
// changes, update every `min-[900px]:` occurrence in this file too.

export function AppBar({
  onDrawerToggle,
  onHome,
  onOpen,
  onSave,
  onImportSelection,
  onExportSelection,
  onWeboImport,
  onSettings,
  onAbout,
  onLegal,
  onCookieSettings,
}: {
  onDrawerToggle: () => void;
  onHome: () => void;
  onOpen: () => void;
  onSave: () => void;
  onImportSelection: () => void;
  onExportSelection: () => void;
  onWeboImport: () => void;
  onSettings: () => void;
  onAbout: () => void;
  onLegal: (kind: LegalKind) => void;
  onCookieSettings: () => void;
}) {
  useLang();

  return (
    <>
      {/* Desktop has the persistent sidebar (TabBar) instead — no drawer to open. */}
      <Button
        variant="ghost"
        size="icon"
        className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground min-[900px]:hidden"
        aria-label={t('app.drawerMenu')}
        onClick={onDrawerToggle}
      >
        <MenuIcon />
      </Button>
      <h1 className="text-sm font-bold whitespace-nowrap text-primary-foreground">
        <button
          type="button"
          onClick={onHome}
          title={t('nav.home')}
          className="flex items-baseline gap-1.5 rounded-sm hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-foreground"
        >
          {t('app.title')}{' '}
          <span className="hidden font-mono text-[10px] font-medium tracking-widest text-primary-foreground/55 uppercase min-[900px]:inline">
            {t('app.subtitle')}
          </span>
        </button>
      </h1>
      <span className="flex-1" />
      {/* Desktop has room to spell all four actions out — only mobile
          collapses them behind the "⋮" menu. */}
      <div className="hidden items-center gap-1 min-[900px]:flex">
        <Button variant="ghost" size="sm" className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground" onClick={onOpen}>
          {t('app.open')}
        </Button>
        <Button variant="ghost" size="sm" className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground" onClick={onSave}>
          {t('app.save')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground"
          title={t('export.workspace.import')}
          onClick={onImportSelection}
        >
          {t('app.importSelection')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground"
          title={t('export.workspace.exportSelection')}
          onClick={onExportSelection}
        >
          {t('app.exportSelection')}
        </Button>
        <Button variant="ghost" size="sm" className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground" onClick={onWeboImport}>
          {t('app.weboImport')}
        </Button>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="text-primary-foreground/80 hover:bg-white/10 hover:text-primary-foreground min-[900px]:hidden" aria-label={t('app.menu')}>
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onOpen}>{t('app.open')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onSave}>{t('app.save')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onImportSelection}>{t('app.importSelection')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onExportSelection}>{t('app.exportSelection')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onWeboImport}>{t('app.weboImport')}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSettings}>{t('app.settings')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onAbout}>{t('app.about')}</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onLegal('impressum')}>{t('nav.impressum')}</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onLegal('datenschutz')}>{t('nav.datenschutz')}</DropdownMenuItem>
          <DropdownMenuItem onClick={onCookieSettings}>{t('consent.title')}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
